import React, { useRef, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { PageSubTitle, LinkBack } from 'components/common/Elements/Elements';

import { useRegion } from 'contexts/RegionContext';
import { access, hasAccess, resources } from 'utils/permissions';

import classes from './PlatformFeatureFlags.module.scss';
import FeatureFlagsTab from './parts/FeatureFlagsTab';
import ActivityLogTab from './parts/ActivityLogTab';

const FLAGS_TAB = 'Feature Flags';
const LOG_TAB = 'Activity Log';

const slug = (tab: string) => tab.toLowerCase().replace(/\s+/g, '-');

const PlatformFeatureFlags = ({ customerId }: any) => {
  // Named in the assign confirmation copy.
  const { region } = useRegion();
  const canWrite = hasAccess([resources.PRODUCT_FEATURE_FLAG, access.WRITE]);
  const canReadLogs = hasAccess([
    resources.PRODUCT_FEATURE_FLAG_LOGS,
    access.READ
  ]);

  const [activeTab, setActiveTab] = useState(FLAGS_TAB);
  const isFlagsTab = activeTab === FLAGS_TAB;

  const tabs = canReadLogs ? [FLAGS_TAB, LOG_TAB] : [FLAGS_TAB];

  // Roving tabindex: only the selected tab is in the tab order, and the arrow
  // keys move between them. Without this a screen reader announces two
  // unlabelled buttons with no indication that they are alternatives.
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const selectTab = (tab: string) => {
    setActiveTab(tab);
    tabRefs.current[tab]?.focus();
  };

  const handleTabKeys = (event: React.KeyboardEvent) => {
    const step =
      // eslint-disable-next-line no-nested-ternary
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;

    if (!step) return;

    event.preventDefault();

    const next = (tabs.indexOf(activeTab) + step + tabs.length) % tabs.length;

    selectTab(tabs[next] || activeTab);
  };

  return (
    <>
      <div className={classes.header}>
        <PageSubTitle title="Feature Flags" />
        <LinkBack text="Platform" />
      </div>

      <nav
        className={classes.tabs}
        role="tablist"
        aria-label="Feature Flags views"
        onKeyDown={handleTabKeys}
      >
        {tabs.map(tab => (
          <button
            key={tab}
            ref={node => {
              tabRefs.current[tab] = node;
            }}
            type="button"
            role="tab"
            id={`ff-tab-${slug(tab)}`}
            aria-selected={tab === activeTab}
            // Only one panel is rendered at a time, so pointing an inactive
            // tab at a non-existent id would be worse than omitting it.
            aria-controls={
              tab === activeTab ? `ff-panel-${slug(tab)}` : undefined
            }
            tabIndex={tab === activeTab ? 0 : -1}
            onClick={() => selectTab(tab)}
            className={cn(
              classes.tabs_item,
              tab === activeTab && classes.tabs_item__active
            )}
          >
            {tab}
          </button>
        ))}
      </nav>

      {/* Each tab owns its own useDataGrid. The hook seeds its column state
          with useState, so a single instance handed a different config on tab
          change would keep the first tab's columns. */}
      <div
        role="tabpanel"
        id={`ff-panel-${slug(activeTab)}`}
        aria-labelledby={`ff-tab-${slug(activeTab)}`}
      >
        {isFlagsTab ? (
          <FeatureFlagsTab
            customerId={customerId}
            canWrite={canWrite}
            region={region?.fullName || ''}
          />
        ) : (
          <ActivityLogTab customerId={customerId} />
        )}
      </div>
    </>
  );
};

PlatformFeatureFlags.propTypes = {
  customerId: PropTypes.string
};

export default PlatformFeatureFlags;
